import { useCallback, useEffect, useState } from 'react';
import { Card, Modal, FormField, TextInput, StatusBadge, Drawer, Toggle, PageHeader } from '@/components/ui';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

interface TrunkSettings {
  from_user?: string;
  from_domain?: string;
  register?: boolean;
  identify_ip?: string;
  context?: string;
  static?: boolean;
  asterisk_endpoint?: string;
  note?: string;
}

// A static trunk is defined in pjsip.conf (e.g. shared Mvoice) — read-only.
const isStatic = (t: Trunk) => !!(t.settings?.static || t.settings?.asterisk_endpoint);

interface Trunk {
  id: number;
  name: string;
  provider: string;
  host: string;
  port: number;
  username?: string | null;
  status: string;
  tenant_id?: number | null;
  settings?: TrunkSettings | null;
}

// Live PJSIP state from Asterisk (online = trunk usable for calls).
const LIVE_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  online: 'ok',
  offline: 'danger',
  unknown: 'neutral',
};
const LIVE_LABEL: Record<string, string> = {
  online: 'מחובר',
  offline: 'מנותק',
  unknown: 'לא ידוע',
};

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  registered: 'ok',
  configured: 'neutral',
  degraded: 'warn',
  failed: 'danger',
};

const STATUS_LABEL: Record<string, string> = {
  registered: 'רשום',
  configured: 'הוגדר',
  degraded: 'מדורדר',
  failed: 'נכשל',
};

const EMPTY_FORM = { name: '', provider: 'mvoice', host: '', port: 5060, username: '', password: '' };

export function Trunks() {
  const [rows, setRows] = useState<Trunk[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Trunk | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Trunk | null>(null);
  const [drawer, setDrawer] = useState<Trunk | null>(null);
  const [testing, setTesting] = useState<number | null>(null);
  const [live, setLive] = useState<Record<number, { state: string; channels: number }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await superApi.trunks()) as Trunk[]);
    } catch {
      toast('שגיאה בטעינת ה-trunks', 'danger');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLive = useCallback(async () => {
    try {
      const s = await superApi.trunksStatus();
      setLive(Object.fromEntries(s.map((x) => [x.id, { state: x.endpoint_state, channels: x.active_channels }])));
    } catch {
      /* non-fatal — live status is best-effort */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll live PJSIP state every 15s so online/offline reflects Asterisk.
  useEffect(() => {
    void loadLive();
    const h = setInterval(loadLive, 15000);
    return () => clearInterval(h);
  }, [loadLive]);

  async function handleTest(t: Trunk) {
    setTesting(t.id);
    try {
      const res = await superApi.testTrunk(t.id);
      if (res?.online) {
        toast('ה-trunk מחובר ופעיל ב-Asterisk', 'ok');
      } else if (res?.reachable) {
        toast(`הספק נגיש (${res.latency_ms}ms) אך עדיין לא רשום`, 'warn');
      } else {
        toast(`לא ניתן להתחבר: ${res?.error ?? 'נכשל'}`, 'danger');
      }
      void loadLive();
      // reflect new status locally
      if (res?.status) {
        setRows((prev) => prev.map((r) => (r.id === t.id ? { ...r, status: res.status } : r)));
        setDrawer((d) => (d && d.id === t.id ? { ...d, status: res.status } : d));
      }
    } catch {
      toast('שגיאה בבדיקת החיבור', 'danger');
    } finally {
      setTesting(null);
    }
  }

  async function handleDelete(t: Trunk) {
    try {
      await superApi.deleteTrunk(t.id);
      toast('ה-trunk נמחק', 'ok');
      setDeleteTarget(null);
      setDrawer((d) => (d && d.id === t.id ? null : d));
      setRows((prev) => prev.filter((r) => r.id !== t.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="קווי SIP" actions={<>
        <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>
          + trunk חדש
        </button>
      </>} />

      {loading ? (
        <Card>טוען…</Card>
      ) : rows.length === 0 ? (
        <Card>אין trunks מוגדרים. צור חדש כדי להתחיל.</Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--gap)' }}>
          {rows.map((t) => (
            <Card key={t.id}>
              <div onClick={() => setDrawer(t)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                  <strong>{t.name}</strong>
                  {live[t.id] ? (
                    <StatusBadge tone={LIVE_TONE[live[t.id].state] ?? 'neutral'} dot size="sm">
                      {LIVE_LABEL[live[t.id].state] ?? live[t.id].state}
                      {live[t.id].channels > 0 ? ` · ${live[t.id].channels}` : ''}
                    </StatusBadge>
                  ) : (
                    <StatusBadge tone={STATUS_TONE[t.status] ?? 'neutral'} dot size="sm">
                      {STATUS_LABEL[t.status] ?? t.status}
                    </StatusBadge>
                  )}
                </div>
                <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }} dir="ltr">
                  {t.provider} · {t.host}:{t.port}
                </div>
                {isStatic(t) && (
                  <div style={{ marginTop: 6 }}>
                    <StatusBadge tone="neutral" size="sm">מערכת · משותף לכל הלקוחות</StatusBadge>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {createOpen && (
        <TrunkForm
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            void load();
          }}
        />
      )}

      {editTarget && (
        <TrunkForm
          mode="edit"
          trunk={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void load();
          }}
        />
      )}

      {deleteTarget && (
        <Modal
          isOpen
          onClose={() => setDeleteTarget(null)}
          title="מחיקת trunk"
          footer={
            <>
              <button type="button" className="btn" onClick={() => setDeleteTarget(null)}>
                ביטול
              </button>
              <button type="button" className="btn danger" onClick={() => handleDelete(deleteTarget)}>
                מחק
              </button>
            </>
          }
        >
          <p>
            למחוק את ה-trunk <strong>{deleteTarget.name}</strong>? שיחות יוצאות דרכו יפסיקו לעבוד.
          </p>
        </Modal>
      )}

      <Drawer isOpen={!!drawer} onClose={() => setDrawer(null)} title={drawer?.name}>
        {drawer && (
          <div className="stack">
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', fontSize: 'var(--t-sm)' }}>
              <span style={{ color: 'var(--muted)' }}>ספק:</span>
              <span>{drawer.provider}</span>
              <span style={{ color: 'var(--muted)' }}>Host:</span>
              <span dir="ltr">{drawer.host}</span>
              <span style={{ color: 'var(--muted)' }}>Port:</span>
              <span dir="ltr">{drawer.port}</span>
              {drawer.username && (
                <>
                  <span style={{ color: 'var(--muted)' }}>Username:</span>
                  <span dir="ltr">{drawer.username}</span>
                </>
              )}
              <span style={{ color: 'var(--muted)' }}>סטטוס:</span>
              <span>
                <StatusBadge tone={STATUS_TONE[drawer.status] ?? 'neutral'} dot size="sm">
                  {STATUS_LABEL[drawer.status] ?? drawer.status}
                </StatusBadge>
              </span>
              <span style={{ color: 'var(--muted)' }}>מצב חי:</span>
              <span>
                <StatusBadge tone={LIVE_TONE[live[drawer.id]?.state] ?? 'neutral'} dot size="sm">
                  {LIVE_LABEL[live[drawer.id]?.state] ?? '—'}
                  {live[drawer.id]?.channels ? ` · ${live[drawer.id].channels} שיחות` : ''}
                </StatusBadge>
              </span>
            </div>
            {isStatic(drawer) && (
              <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: '8px 0 0' }}>
                קו מערכת המוגדר ב-pjsip.conf ומשותף לכל הלקוחות (לקריאה בלבד).
              </p>
            )}
            <div style={{ marginTop: 12 }}>
              <strong style={{ fontSize: 'var(--t-sm)' }}>פעולות:</strong>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {!isStatic(drawer) && (
                  <button type="button" className="btn" onClick={() => setEditTarget(drawer)}>
                    ערוך
                  </button>
                )}
                <button type="button" className="btn" disabled={testing === drawer.id} onClick={() => handleTest(drawer)}>
                  {testing === drawer.id ? 'בודק…' : 'בדיקת חיבור'}
                </button>
                {!isStatic(drawer) && (
                  <button type="button" className="btn danger" onClick={() => setDeleteTarget(drawer)}>
                    מחק
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

function TrunkForm({
  mode,
  trunk,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  trunk?: Trunk;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: trunk?.name ?? EMPTY_FORM.name,
    provider: trunk?.provider ?? EMPTY_FORM.provider,
    host: trunk?.host ?? EMPTY_FORM.host,
    port: trunk?.port ?? EMPTY_FORM.port,
    username: trunk?.username ?? '',
    password: '',
    from_user: trunk?.settings?.from_user ?? '',
    from_domain: trunk?.settings?.from_domain ?? '',
    identify_ip: trunk?.settings?.identify_ip ?? '',
    register: trunk?.settings?.register ?? false,
  });
  const [saving, setSaving] = useState(false);

  function buildSettings(): TrunkSettings {
    return {
      ...(form.from_user ? { from_user: form.from_user } : {}),
      ...(form.from_domain ? { from_domain: form.from_domain } : {}),
      ...(form.identify_ip ? { identify_ip: form.identify_ip } : {}),
      register: form.register,
    };
  }

  async function save() {
    if (!form.name || !form.host) {
      toast('יש למלא שם ו-host', 'danger');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'create') {
        await superApi.createTrunk({
          name: form.name,
          provider: form.provider,
          host: form.host,
          port: Number(form.port),
          username: form.username || null,
          password: form.password || null,
          settings: buildSettings(),
        });
        toast('נוצר', 'ok');
      } else if (trunk) {
        const patch: Record<string, unknown> = {
          name: form.name,
          host: form.host,
          port: Number(form.port),
          username: form.username || null,
          settings: buildSettings(),
        };
        if (form.password) patch.password = form.password;
        await superApi.updateTrunk(trunk.id, patch);
        toast('עודכן', 'ok');
      }
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === 'create' ? 'Trunk חדש' : `עריכת ${trunk?.name}`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            ביטול
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'שומר…' : 'שמור'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="שם" required>
          <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FormField>
        <FormField label="ספק" hint={mode === 'edit' ? 'הספק לא ניתן לשינוי' : undefined}>
          <TextInput
            value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
            dir="ltr"
            disabled={mode === 'edit'}
          />
        </FormField>
        <FormField label="Host" required>
          <TextInput value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} dir="ltr" />
        </FormField>
        <FormField label="Port">
          <TextInput type="number" value={String(form.port)} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} dir="ltr" />
        </FormField>
        <FormField label="Username">
          <TextInput value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} dir="ltr" />
        </FormField>
        <FormField label="Password" hint={mode === 'edit' ? 'השאר ריק כדי לא לשנות' : undefined}>
          <TextInput type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} dir="ltr" />
        </FormField>

        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 4 }}>
          <strong style={{ fontSize: 'var(--t-sm)' }}>הגדרות מתקדמות</strong>
        </div>
        <FormField label="From User" hint="ברירת מחדל: שם המשתמש">
          <TextInput value={form.from_user} onChange={(e) => setForm({ ...form, from_user: e.target.value })} dir="ltr" />
        </FormField>
        <FormField label="From Domain" hint="ברירת מחדל: ה-host">
          <TextInput value={form.from_domain} onChange={(e) => setForm({ ...form, from_domain: e.target.value })} dir="ltr" />
        </FormField>
        <FormField label="Identify IP" hint="IP/CIDR לזיהוי שיחות נכנסות מהספק (ברירת מחדל: ה-host)">
          <TextInput value={form.identify_ip} onChange={(e) => setForm({ ...form, identify_ip: e.target.value })} dir="ltr" />
        </FormField>
        <Toggle
          checked={form.register}
          onChange={(v) => setForm({ ...form, register: v })}
          label="להירשם לספק (REGISTER) — נדרש כשהספק לא מזהה לפי IP"
        />
      </div>
    </Modal>
  );
}
